/**
 * The Scenarios tab: what routes work orders to this procedure.
 *
 * A scenario is a separate record from the procedure — the service keeps
 * routing apart from what Radar then does — so this reads and writes the
 * scenario, not the plan.
 *
 * It reads, then edits in place: viewing is the common case, and the pickers
 * are heavy enough that mounting them only on Edit keeps the tab quick to open.
 *
 * The read view draws scope the way Troubleshooting's associations do, because
 * it is the same thing being shown: problem types through
 * `ProblemTypeHierarchicalDisplay`, which fetches the hierarchy itself and
 * collapses a fully-selected parent, and priorities as the coloured pill the
 * priority's own palette gives it.
 *
 * Scope comes back as bare ids. Problem-type names ride in with the hierarchy;
 * priorities still need hydrating with `readMultiple`, and until a name has
 * loaded its id is shown as `#12`.
 */

import { Alert, Button, Form, Spin, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import AOPLoadFailed from './AOPLoadFailed';
import { withRouter } from 'react-router';
import { AOPActiveWindow, AOPScenario, AOPScope } from '../../types/AOPTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import OWButton from '../ow_button/OWButton';
import OWTag from '../common/OWTag';
import SelectWithAllToggle from '../common/SelectWithAllToggle';
import ProblemTypeHierarchicalDisplay from '../problem_type_hierarchical_display/ProblemTypeHierarchicalDisplay';
import TSPriorityDisplay from '../buyer_admin_settings_page/troubleshooting/TSPriorityDisplay';
import { workOrderPrioritiesRestCrudThunksForBuyer } from '../../thunks/work_order_priorities_thunks';
import {
	describeScenarioError,
	readAgentScenarios,
	updateAgentScenario,
} from '../../thunks/agent_scenarios_thunks';
import { AOPActiveWindowField, validateActiveWindow } from './AOPActiveWindowField';
import { AOPPrioritySelect, AOPProblemTypeSelect } from './AOPScopeFields';
import { activeWindowLabel } from './utils/AOPScenarioUtils';

/*
  Not the list page's collection.

  These screens want one thing from `GET /scenarios` — the entry for a given
  procedure. Sharing `agentScenariosIndex` with the paginated list meant an
  unpaginated read overwriting its page and its count, which is the
  shared-collection trap CLAUDE.md names.

  The entry is resolved through this collection's ids rather than the slice's
  `records`, which is merged into and never pruned: a scenario deleted in
  another session would otherwise survive a refetch that no longer returns it.
*/
const SCENARIO_TC = 'aopProcedureScenario';
const PRIORITY_TC = 'aopPriorities';
const FORM_NAME = 'aop-associations-form';

const toScope = (isAll: boolean, ids: number[]): AOPScope => (isAll ? 'all' : ids ? ids : []);
const idsOf = (scope: AOPScope): number[] => (scope === 'all' || !scope ? [] : scope);

const ReadRow: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className="flex items-start gap-m border-0 border-b border-solid border-grey-4 py-m">
		<span className="bodyregular w-[180px] shrink-0 text-character-primary">{label}</span>
		<div className="flex min-w-0 flex-1 flex-col gap-2xs">{children}</div>
	</div>
);

const AOPScenariosTab: FC<any> = ({
	procedureId,
	scenario,
	isLoading,
	didReadFail,
	priorityRecords,
	loadScenarios,
	saveScenario,
	hydratePriorities,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [isEditing, setIsEditing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const problemTypeIds = scenario ? idsOf(scenario.problem_type_ids) : [];
	const priorityIds = scenario ? idsOf(scenario.priority_ids) : [];
	const priorityKey = priorityIds.join(',');

	// Reading the records behind a set of ids is a fetch, not something derivable
	// from props, so it belongs in an effect. Keyed on the ids themselves so it
	// runs once per set rather than on every render. Problem types need no
	// equivalent: the hierarchical display fetches the whole tree itself and
	// caches it for every instance on the page.
	useEffect(() => {
		if (priorityKey) {
			hydratePriorities(priorityKey.split(',').map(Number));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [priorityKey]);

	const startEditing = useCallback(() => {
		setError(null);
		form.setFieldsValue({
			problemTypeIds,
			priorityIds,
			allProblemTypes: scenario ? scenario.problem_type_ids === 'all' : false,
			allPriorities: scenario ? scenario.priority_ids === 'all' : false,
			activeWindow: scenario ? scenario.active_window : null,
		});
		setIsEditing(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form, scenario]);

	const onSubmit = useCallback(
		(values) => {
			setError(null);
			setIsSaving(true);

			saveScenario(scenario.id, {
				problem_type_ids: toScope(values.allProblemTypes, values.problemTypeIds),
				priority_ids: toScope(values.allPriorities, values.priorityIds),
				active_window: values.activeWindow as AOPActiveWindow,
			})
				.then(() => {
					/*
					  The write has landed. Reading the scenario back only catches the
					  form up, so its failure is its own — it used to reach the catch
					  below and say "Could not save the scenario" about a change the
					  service had already taken, which invited the author to submit it
					  a second time.
					*/
					loadScenarios(procedureId).catch(() =>
						message.error('Saved, but the page could not refresh.')
					);

					setIsSaving(false);
					setIsEditing(false);
					message.success('Associations updated successfully!');
				})
				// Only the write reaches this now.
				.catch((code) => {
					setIsSaving(false);
					setError(describeScenarioError(code));
				});
		},
		[loadScenarios, procedureId, saveScenario, scenario]
	);

	if (isLoading && !scenario) {
		return (
			<div className="flex justify-center p-l">
				<Spin tip="Loading associations…" />
			</div>
		);
	}

	/*
	  A read that failed is not an answer.

	  The detail page loads this and swallows the failure, so a transient
	  `GET /scenarios` error used to reach here as an empty store and be
	  reported as "nothing routes to this procedure" — a statement about the
	  author's configuration, made on no evidence. Saying so also hides the
	  scenario the header needs in order to delete the pair.
	*/
	if (!scenario && didReadFail) {
		return (
			<div className="p-l">
				<AOPLoadFailed
					message="Could not load what routes to this procedure"
					description="The scenario could not be read, so there is nothing to show yet. This does not mean the procedure has none."
					isRetrying={isLoading}
					onRetry={() => loadScenarios(procedureId).catch(() => undefined)}
				/>
			</div>
		);
	}

	if (!scenario) {
		return (
			<div className="p-l">
				<Alert
					type="warning"
					showIcon
					message="Nothing routes to this procedure"
					description="No scenario points at it, so Radar will never pick a work order up with it. The service has no way to list procedures, so a procedure with no scenario is also invisible on the list page."
				/>
			</div>
		);
	}

	return (
		<div className="flex flex-col p-l">
			<div className="mb-l flex items-start gap-xs">
				<div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-xs">
					<span className="bodymedium text-character-primary">Associations</span>
					<span className="bodyregular text-character-secondary">
						View, add, or change the problem types and priorities linked to this procedure
					</span>
				</div>
				{isEditing ? null : (
					<Button icon={<EditOutlined />} onClick={startEditing}>
						Edit
					</Button>
				)}
			</div>

			{isEditing ? (
				// Vertical: `SelectWithAllToggle` draws its own label row with the
				// Select All switch on the right, so a side label on the one plain
				// field would be the only one not stacked.
				<Form
					form={form}
					id={FORM_NAME}
					layout="vertical"
					requiredMark
					className="max-w-[1100px]"
					onFinish={onSubmit}
				>
					<SelectWithAllToggle
						label="Problem types"
						name="problemTypeIds"
						allName="allProblemTypes"
						form={form}
						displaySelectAllWithLabel
						required
						requiredIfNotAllSelected
						selectAllPlaceholder="All problem types"
					>
						<AOPProblemTypeSelect />
					</SelectWithAllToggle>

					<SelectWithAllToggle
						label="Priorities"
						name="priorityIds"
						allName="allPriorities"
						form={form}
						displaySelectAllWithLabel
						required
						requiredIfNotAllSelected
						selectAllPlaceholder="All priorities"
					>
						<AOPPrioritySelect />
					</SelectWithAllToggle>

					<Form.Item
						className="mb-l"
						name="activeWindow"
						label="Active window"
						required
						rules={[
							{
								validator: (_, value) => {
									const problem = validateActiveWindow(value);
									return problem ? Promise.reject(new Error(problem)) : Promise.resolve();
								},
							},
						]}
					>
						<AOPActiveWindowField />
					</Form.Item>

					{error ? <Alert className="mb-m" type="error" showIcon message={error} /> : null}

					<div className="mt-m flex gap-xs">
						<OWButton type="primary" htmlType="submit" form={FORM_NAME} loading={isSaving}>
							Apply changes
						</OWButton>
						<OWButton onClick={() => setIsEditing(false)}>Cancel</OWButton>
					</div>
				</Form>
			) : (
				<div className="max-w-[1100px]">
					<ReadRow label="Problem types">
						{/*
						  `null` is this component's own word for "every problem type", which
						  is exactly what the `all` scope means — so the two line up without
						  a special case here.
						*/}
						<ProblemTypeHierarchicalDisplay
							displayMode="pill"
							problemTypeIds={scenario.problem_type_ids === 'all' ? null : problemTypeIds}
						/>
					</ReadRow>

					<ReadRow label="Priorities">
						<div className="flex flex-row flex-wrap items-center gap-2">
							{scenario.priority_ids === 'all' ? (
								<OWTag>All Priorities</OWTag>
							) : (
								priorityIds.map((id) => {
									// Bracket access, not a `nullSafeGet` path: the keys are ids.
									const priority = priorityRecords ? priorityRecords[id] : null;
									return priority ? (
										<TSPriorityDisplay key={id} priority={priority} />
									) : (
										<OWTag key={id}>{`#${id}`}</OWTag>
									);
								})
							)}
						</div>
					</ReadRow>

					<ReadRow label="Active window">
						<div className="flex flex-row flex-wrap items-center gap-2">
							<OWTag>{activeWindowLabel(scenario.active_window)}</OWTag>
						</div>
					</ReadRow>
				</div>
			)}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => {
	const procedureId = nullSafeGet('match.params.id', ownProps);
	const scenariosSlice = nullSafeGetOrElse('agent_scenarios', state, {});
	const scenarioRecords = nullSafeGetOrElse('records', scenariosSlice, {});
	// Bracket access for the collection: a target collection's name is a key on
	// the slice, not a path segment to interpolate.
	const scenarioCollection = scenariosSlice[SCENARIO_TC];
	// Through the collection's ids, like the detail page: `records` keeps a
	// scenario another session deleted, and this tab would go on editing it.
	const entry = nullSafeGetOrElse('recordsIds', scenarioCollection, [] as string[])
		.map((id) => scenarioRecords[id])
		.filter((record) => record && record.procedure && record.procedure.id === procedureId)[0];

	return {
		procedureId,
		scenario: entry ? (entry.scenario as AOPScenario) : null,
		isLoading: nullSafeGetOrElse('agent_scenarios.fetching', state, false),
		/*
		  Whether the last read of this collection failed. The reducer clears it
		  on a success and appends on a failure, so an empty store with an error
		  behind it can be told from an empty store that is the answer.
		*/
		didReadFail: nullSafeGetOrElse('fetchErrors', scenarioCollection, []).length > 0,
		// Bracket access against the whole slice, not a `nullSafeGet` path: the
		// keys are record ids, not path segments.
		priorityRecords: nullSafeGetOrElse('work_order_priorities.records', state, {}),
	};
};

const mapDispatchToProps = (dispatch) => ({
	loadScenarios: (procedureId: string) =>
		dispatch(
			/*
			  One request, one record. `GET /scenarios` takes `procedure_id`, so
			  this asks for the scenario routing to this procedure rather than
			  reading the list and looking through it.
			*/
			readAgentScenarios(
				{ procedure_id: procedureId },
				SCENARIO_TC,
				{ current: 1, pageSize: 1 },
				null,
				null,
				false
			)
		),
	saveScenario: (scenarioId: string, input: any) =>
		dispatch(updateAgentScenario(scenarioId, input)),
	hydratePriorities: (ids: number[]) =>
		dispatch(workOrderPrioritiesRestCrudThunksForBuyer.readMultiple(ids, PRIORITY_TC)),
});

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(AOPScenariosTab));
